import {
  SiteArtifact,
  useSiteArtifacts,
} from "@inrange/building-manager-api-client";
import { Site } from "@inrange/building-manager-api-client/models-site";
import { ChangeEvent, MouseEvent, useRef, useState } from "react";
import { Button, Card, Table } from "react-bootstrap";
import Loading from "../../Loading";
import { epochSecondsToDate } from "./utils";

const SiteArtifactRow = ({
  artifact,
  onDownload,
  onDelete,
  isDeleting,
}: {
  artifact: SiteArtifact;
  onDownload: (artifact: SiteArtifact) => void;
  onDelete: (artifact: SiteArtifact) => void;
  isDeleting: boolean;
}) => {
  return (
    <tr>
      <td>{artifact.filename}</td>
      <td>{artifact.notes}</td>
      <td>{artifact.user}</td>
      <td>{epochSecondsToDate(artifact.uploadedAt)}</td>
      <td>
        <Button
          variant="primary"
          className="ms-2"
          onClick={() => onDownload(artifact)}
          data-testid={`${artifact.filename}-download`}
        >
          Download
        </Button>
        <Button
          variant="danger"
          className="ms-2"
          onClick={() => onDelete(artifact)}
          disabled={isDeleting}
        >
          Delete
        </Button>
      </td>
    </tr>
  );
};

const SiteArtifacts = ({ site }: { site: Site }) => {
  const { fetchArtifacts, uploadArtifact, deleteArtifact, getDownloadUrl } =
    useSiteArtifacts({
      siteId: site.id,
    });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const artifacts = fetchArtifacts.data?.artifacts || [];

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      await uploadArtifact.mutateAsync(file);
    } catch (error) {
      alert(`Error uploading file: ${error}`);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUploadClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    fileInputRef.current?.click();
  };

  const handleDownload = async (artifact: SiteArtifact) => {
    try {
      const url = await getDownloadUrl(artifact.guid);

      // Create a download link and click it
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", artifact.filename);
      document.body.appendChild(link);
      link.click();

      // Clean up and remove the link
      link.parentNode?.removeChild(link);
    } catch (error) {
      alert(`Error downloading file: ${error}`);
    }
  };

  const handleDelete = async (artifact: SiteArtifact) => {
    if (!confirm(`Are you sure you want to delete "${artifact.filename}"?`)) {
      return;
    }

    try {
      await deleteArtifact.mutateAsync(artifact.guid);
    } catch (error) {
      alert(`Error deleting file: ${error}`);
    }
  };

  return (
    <Card body className="mt-2">
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <strong>Attached reports ({artifacts.length})</strong>
        <div>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <Button
            variant="primary"
            onClick={handleUploadClick}
            disabled={isUploading}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </div>
      </div>
      {fetchArtifacts.isLoading && <Loading label="Loading artifacts..." />}
      {fetchArtifacts.isError && (
        <div className="text-danger mt-2">Error loading artifacts.</div>
      )}
      {!fetchArtifacts.isLoading && artifacts.length > 0 && (
        <Table bordered size="sm" responsive style={{ margin: "8px 0 0 0" }}>
          <thead>
            <tr>
              <th>File name</th>
              <th>Notes</th>
              <th>Uploaded by</th>
              <th>Uploaded at</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {artifacts
              .sort((a, b) => b.uploadedAt - a.uploadedAt)
              .map((artifact) => (
                <SiteArtifactRow
                  key={artifact.guid}
                  artifact={artifact}
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                  isDeleting={deleteArtifact.isLoading}
                />
              ))}
          </tbody>
        </Table>
      )}
      {!fetchArtifacts.isLoading && artifacts.length === 0 && (
        <div className="text-muted mt-2">No attached reports uploaded.</div>
      )}
    </Card>
  );
};

export default SiteArtifacts;
